import type { Score } from '../types';
import { isSameDay, subDays } from 'date-fns';

export function calculateScore(journalEntries: any[] | null | undefined, lessonsLearned: any[] | null | undefined, tasks: any[] | null | undefined, projects: any[] | null | undefined): Score {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Ensure arrays are defined
  const safeJournalEntries = journalEntries ?? [];
  const safeLessons = lessonsLearned ?? [];
  const safeTasks = tasks ?? [];
  const safeProjects = projects ?? [];

  // Calculate journal streak
  let journalStreak = 0;
  let currentDate = new Date(startOfDay);
  currentDate.setDate(currentDate.getDate() - 1); // Start checking from yesterday
  
  // Check if there's an entry for today
  const hasEntryToday = safeJournalEntries.some(entry => {
    const entryDate = new Date(entry.created_at);
    return isSameDay(entryDate, startOfDay);
  });
  
  // If there's an entry today, include it in the streak
  if (hasEntryToday) {
    journalStreak = 1;
    currentDate = subDays(startOfDay, 1); // Start checking from yesterday
  } else {
    currentDate = startOfDay; // Start checking from today
  }
  
  // Check for consecutive days before today
  while (true) {
    const hasEntry = safeJournalEntries.some(entry => {
      const entryDate = new Date(entry.created_at);
      return isSameDay(entryDate, currentDate);
    });
    
    if (!hasEntry) break;
    journalStreak++;
    currentDate = subDays(currentDate, 1);
  }

  // Calculate lessons streak using the same approach
  let lessonsStreak = 0;
  currentDate = new Date(startOfDay);
  currentDate.setDate(currentDate.getDate() - 1); // Start checking from yesterday
  
  // Check if there's a lesson for today
  const hasLessonToday = safeLessons.some(lesson => {
    const lessonDate = new Date(lesson.created_at);
    return isSameDay(lessonDate, startOfDay);
  });
  
  // If there's a lesson today, include it in the streak
  if (hasLessonToday) {
    lessonsStreak = 1;
    currentDate = subDays(startOfDay, 1); // Start checking from yesterday
  } else {
    // For lessons, we don't reset the streak if there's no lesson today
    // We only check from yesterday backwards
    currentDate = subDays(startOfDay, 1); // Start checking from yesterday
  }
  
  // Check for consecutive days before yesterday
  while (true) {
    const hasLesson = safeLessons.some(lesson => {
      const lessonDate = new Date(lesson.created_at);
      return isSameDay(lessonDate, currentDate);
    });
    
    if (!hasLesson) break;
    lessonsStreak++;
    currentDate = subDays(currentDate, 1);
  }

  // Calculate action streak using the same approach
  let actionStreak = 0;
  currentDate = new Date(startOfDay);
  currentDate.setDate(currentDate.getDate() - 1); // Start checking from yesterday
  
  // Check if there's a completed task for today
  const hasCompletedTaskToday = safeTasks.some(task => {
    if (!task.completed || !task.completed_at) return false;
    const taskDate = new Date(task.completed_at);
    return isSameDay(taskDate, startOfDay);
  });
  
  // If there's a completed task today, include it in the streak
  if (hasCompletedTaskToday) {
    actionStreak = 1;
    currentDate = subDays(startOfDay, 1); // Start checking from yesterday
  } else {
    currentDate = startOfDay; // Start checking from today
  }
  
  // Check for consecutive days before today
  while (true) {
    const hasCompletedTask = safeTasks.some(task => {
      if (!task.completed || !task.completed_at) return false;
      const taskDate = new Date(task.completed_at);
      return isSameDay(taskDate, currentDate);
    });
    
    if (!hasCompletedTask) break;
    actionStreak++;
    currentDate = subDays(currentDate, 1);
  }

  // Calculate task metrics
  const totalTasks = safeTasks.length;
  const completedTasks = safeTasks.filter(task => task.completed).length;

  // Calculate completed projects
  const completedProjects = safeProjects.filter(project => project.status === 'completed').length;

  return {
    journalStreak,
    lessonsStreak,
    actionStreak,
    totalActions: totalTasks,
    completedActions: completedTasks,
    completedProjects
  };
}